import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { AbstractControl, FormControl } from "@angular/forms";
import { map, startWith } from "rxjs/operators";
import { G_PREFIX } from "../constants";
import { UpdateHostClassService } from "../services/update-host-class.service";

import { SelectableValueDirective } from "./selectable-value.directive";

// tslint:disable-next-line:no-conflicting-lifecycle
@Component({
  selector: "g-select",
  template: `<div
  class="g-select"
  g-outside
  (clickOutside)="focused = false"
  [class.focused]="focused && !readonly"
  [class.active]="control.value"
  [class.disabled]="disabled"
  [class.readonly]="readonly"
  [class.invalid]="control.touched && control.invalid"
  #el
>
  <div
    class="g-select-form-field"
    (click)="!disabled && !readonly && focusInput()"
  >
    <div class="g-select-value" #inputComponent>
      <ng-template
        [ngTemplateOutlet]="
          !!selectedTemplate ? selectedTemplate : placeholderTemplate
        "
      >
      </ng-template>

      <ng-template #placeholderTemplate>
        <p class="g-select-placeholder" g-typography [type]="'subHeadline'">
          {{ placeholder }}
        </p>
      </ng-template>
    </div>
    <div class="g-select-icon">
      <mat-icon>expand_more</mat-icon>
    </div>
    <div
      class="g-select-reset"
      *ngIf="clearButton && !disabled && !readonly && focused && control.value"
      (click)="control.reset()"
    >
      <mat-icon>close</mat-icon>
    </div>
  </div>
  <div
    class="g-select-dropdown"
    g-typography
    [type]="'subHeadline'"
    *ngIf="focused"
  >
    <div
      class="g-select-dropdown-item"
      *ngFor="let item of selectOptionList"
      [class.g-select-dropdown-item-selected]="
        selectedOption && item.value === selectedOption.value
      "
      (click)="onSelect(item)"
    >
      <ng-container *ngTemplateOutlet="item.templateRef"></ng-container>
    </div>
  </div>
</div>
`,
  styles: [`@-webkit-keyframes placeholder-glow{50%{opacity:.2}}@-webkit-keyframes placeholder-wave{100%{-webkit-mask-position:-200% 0;mask-position:-200% 0}}b,b.g-typography,strong,strong.g-typography{font-weight:600}a,a.g-typography{color:var(--color-darkBlue-500);text-decoration:none}.g-typography--large{margin-bottom:.5em;font-size:2.125rem;line-height:1.4;font-weight:700}.g-typography--title1,h1,h1.g-typography--title{margin-bottom:.5em;font-size:1.75rem;line-height:1.4;font-weight:700}.g-typography--title2,h2,h2.g-typography--title{margin-bottom:.5em;font-size:1.375rem;line-height:1.4;font-weight:700}.g-typography--title3,h3,h3.g-typography--title{margin-bottom:.5em;font-size:1.25rem;line-height:1.4;font-weight:600}.g-typography--body{margin-bottom:.5em;font-size:1rem;line-height:1.4;font-weight:400}.g-typography--headline{margin-bottom:.5em;font-size:1.0625rem;line-height:1.4;font-weight:600}.g-typography--subHeadline{margin-bottom:.5em;font-size:.9375rem;line-height:1.4;font-weight:400}.g-typography--small{margin-bottom:.5em;font-size:.8125rem;line-height:1.4;font-weight:400}.g-typography--caption{margin-bottom:.5em;font-size:.75rem;line-height:1.4;font-weight:400}.g-menu-item{display:flex;position:relative;align-items:center;justify-content:flex-start;overflow:hidden;padding:0 16px;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;outline:0;border:none;-webkit-tap-highlight-color:transparent;cursor:pointer;width:100%;text-align:left;box-sizing:border-box;color:inherit;font-size:inherit;background:0 0;text-decoration:none;margin:0;min-height:48px}.g-menu-item:hover:not([disabled]){background:rgba(0,0,0,.04)}.g-menu-item[disabled]{cursor:default;opacity:.38}.g-placeholder{display:inline-block;min-height:1em;vertical-align:middle;cursor:wait;width:100%;background-color:var(--color-grey-500);opacity:.5}.g-placeholder.btn::before{display:inline-block;content:""}.g-placeholder-small{min-height:16px}.g-placeholder-standard{min-height:24px}.g-placeholder-glow .g-placeholder{-webkit-animation:2s ease-in-out infinite placeholder-glow;animation:2s ease-in-out infinite placeholder-glow}@keyframes placeholder-glow{50%{opacity:.2}}.g-placeholder-wave{-webkit-mask-image:linear-gradient(130deg,#000 25%,rgba(0,0,0,.8) 75%,#000 55%);mask-image:linear-gradient(130deg,#000 25%,rgba(0,0,0,.8) 75%,#000 55%);-webkit-mask-size:200% 100%;mask-size:200% 100%;-webkit-animation:2s linear infinite placeholder-wave;animation:2s linear infinite placeholder-wave}@keyframes placeholder-wave{100%{-webkit-mask-position:-200% 0;mask-position:-200% 0}}.g-select{height:60px;padding:0 16px;background:#fff;position:relative;min-width:108px;cursor:pointer}.g-select-form-field{padding-top:24px;padding-bottom:10px;position:relative;height:100%}.g-select-form-field .g-select-value,.g-select-form-field label{position:absolute;top:28px;-webkit-transform:translateY(-50%);transform:translateY(-50%);color:var(--color-grey-500);transition:.4s;overflow-x:hidden;width:calc(100% - 25px);color:var(--color-grey-900);white-space:nowrap;font-size:.9375rem;line-height:1.4;font-weight:600;margin:0}.g-select.invalid{border-color:var(--color-red-500)}.g-select.active label,.g-select.focused label{top:18px}.g-select.disabled{background:#f9f9f9}.g-select.readonly .g-select-value{color:var(--color-grey-600)}.g-select--fill{box-shadow:0 1.00267px 0 #e6e6e6;transition:.4s}.g-select--fill.focused{box-shadow:0 1.00267px 0 var(--color-primary)}.g-select--outline{border:1px solid;border-color:var(--color-grey-200)}.g-select--outline.focused{border-color:var(--color-primary)}.g-select-icon{position:absolute;top:30px;-webkit-transform:translateY(-50%);max-width:25px;right:0;display:inline-flex;color:var(--color-grey-400);transform:translateY(-50%)}.g-select--rounded{border-radius:12px}.g-select--clear.g-select-form-field{padding-right:25px}.g-select .g-input-reset{position:absolute;right:0;top:30px;-webkit-transform:translateY(-50%);transform:translateY(-50%);display:inline-flex;cursor:pointer}.g-select .g-input-reset mat-icon{font-size:16px;height:16px;width:16px;line-height:16px}.g-select-placeholder{color:var(--color-grey-500);margin:0}.g-select-dropdown{position:absolute;top:calc(100% + 4px);left:0;width:100%;z-index:55;padding:5px 0;border-radius:12px;max-height:300px;overflow-y:auto;background:var(--color-white);box-shadow:0 3px 6px -4px #0000001f,0 6px 16px #00000014,0 9px 28px 8px #0000000d}.g-select-dropdown-item{padding:10px 16px;color:var(--color-grey-700)}.g-select-dropdown-item:hover{background:var(--color-grey-50)}.g-select-dropdown-item-selected{color:var(--color-grey-400);background:var(--color-grey-100)!important}`],
  providers: [UpdateHostClassService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectComponent
  implements OnInit, OnChanges, AfterViewInit, DoCheck
{
  @ViewChild("select", { static: false }) select: ElementRef;
  @ViewChild("el", { static: true }) el: ElementRef;
  @ContentChildren(SelectableValueDirective, { descendants: true })
  selectOptionList: QueryList<SelectableValueDirective>;
  @Input() required: boolean;
  @Input() appearance: "fill" | "outline" = "fill";
  @Input() rounded = true;
  @Input() placeholder: string;
  @Input() disabled: boolean;
  @Input() readonly: boolean;
  @Input() clearButton: boolean;
  @Input() prefixPosition: "right" | "left" = "left";
  @Input() value: null;
  @Input() control: AbstractControl = new FormControl(null);
  // tslint:disable-next-line:no-output-on-prefix
  @Output() onChange = new EventEmitter<any>();

  private _selectedOption: SelectableValueDirective;
  private _displayOption: { value: string };
  template: any;

  focused = false;
  selectedTemplate: TemplateRef<any>;
  constructor(
    private _updateHostClassService: UpdateHostClassService,
    private _cdr: ChangeDetectorRef
  ) {}

  get selectedOption() {
    return this._selectedOption;
  }

  get displayOption() {
    return this._displayOption;
  }

  ngOnInit() {
    this.control.valueChanges
      .pipe(
        map((value) => {
          this.onChange.emit(value);
        })
      )
      .subscribe();
    this._cdr.detectChanges();
  }

  private updateClass(): void {
    const prefixCls = G_PREFIX + "-select";
    this._updateHostClassService.updateHostClass(this.el.nativeElement, {
      [`${prefixCls}--${this.appearance}`]: this.appearance,
      [`${prefixCls}--rounded`]: this.rounded,
      [`${prefixCls}--clear`]: this.clearButton,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { value } = changes;
    if (value && !value.firstChange) {
      const selectedOption = this.selectOptionList
        .toArray()
        .find((option) => option.value === value.currentValue);
      if (!selectedOption) {
        return;
      }
      this.onSelect(selectedOption);
    }
  }

  onSelect(selectOption: SelectableValueDirective) {
    this._selectedOption = selectOption;
    this.selectedTemplate = selectOption.templateRef;
    this.control.setValue(selectOption.value);
    this.focused = false;
    this._cdr.detectChanges();
  }

  focusInput() {
    this.focused = true;
  }

  ngAfterViewInit(): void {
    this.updateClass();
    this.selectOptionList.changes
      .pipe(startWith(this.selectOptionList))
      .subscribe(() => {
        if (this.selectOptionList.length) {
          if (this.value) {
            const selectedOption = this.selectOptionList
              .toArray()
              .find((option) => option.value == this.value);
            if (!selectedOption) {
              return;
            }
            this.onSelect(selectedOption);
          }
          if (this.control.value) {
            const selectedOption = this.selectOptionList
              .toArray()
              .find((option) => option.value == this.control.value);
            if (!selectedOption) {
              return;
            }
            this.onSelect(selectedOption);
          }
        }
      });
    this._cdr.detectChanges();
  }

  ngDoCheck() {
    if (this.control && this.control.touched) {
      this._cdr.detectChanges();
    }
  }
}
